import React, { useMemo } from 'react';
import Navbar from 'react-bootstrap/Navbar';
import Nav from 'react-bootstrap/Nav';
import NavDropdown from 'react-bootstrap/NavDropdown';
import Badge from 'react-bootstrap/Badge';
import Container from 'react-bootstrap/Container';
import { Link } from 'react-router-dom';
import intersection from 'lodash/intersection';
import {
    DropdownItem,
    DropdownMenu,
    RoleUtils,
    NavUtils,
    LoruleMenuConfig,
    Portrait,
} from '@galaxy/lorule-react-commons';
import { LoruleNavProps } from '@galaxy/lorule-react-commons/lib/js/components/LoruleNav';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { GRAPH_API_REQUEST } from '@galaxy/lorule-react-commons/lib/js/utils/GraphUtils';
import { InteractionStatus } from '@azure/msal-browser';
import { getEnvironmentName } from '@galaxy/lorule-react-commons/lib/js/utils/NavUtils';

export interface ActionMemoMenuConfig extends LoruleMenuConfig {
    navCssClass?: string;
}

export interface ActionMemoNavProps extends LoruleNavProps {
    componentTabs: ActionMemoMenuConfig[];
}

const getDropDownItems = (userDropDownItems: Array<DropdownItem>, userRoles: Array<string>) =>
    userDropDownItems
        .filter((item) => RoleUtils.checkRole(userRoles, item.roles))
        .map((item) => (
            <NavDropdown.Item
                as="button"
                onClick={item.onClick}
                data-testid={item.testId}
                key={item.key}
            >
                {item.item}
            </NavDropdown.Item>
        ));

/**
 * Creates the additional user drop down items if they were specified
 * @param userScopes - The user's scopes
 * @param userDropDownItems - The user DropdownItems to render
 * @returns An array of JSX Elements that will be rendered in the user dropdown menu if any userDropDownItems were
 * provided, else null.
 */
const getAdditionalUserDropDownItems = (
    userRoles: Array<string>,
    userDropDownItems: Array<DropdownItem> | undefined,
) => {
    if (userDropDownItems) {
        const elements = getDropDownItems(userDropDownItems, userRoles);
        if (elements.length > 0) {
            elements.push(<NavDropdown.Divider key="dropdown-elements-divider" />);
        }
        return elements;
    }

    return null;
};

const getAdditionalDropDownMenus = (
    userRoles: Array<string>,
    dropDownMenus: Array<DropdownMenu> | undefined,
) => {
    if (dropDownMenus) {
        return dropDownMenus
            .filter((menu) => RoleUtils.checkRole(userRoles, menu.roles))
            .map((menu) => (
                <NavDropdown
                    title={menu.title}
                    id={menu.key}
                    key={menu.key}
                    data-testid={menu.testId}
                    className="mobile-hide"
                    alignRight
                >
                    {getDropDownItems(menu.items, userRoles)}
                </NavDropdown>
            ));
    }
    return null;
};

const ActionMemoNavHeader = ({
    defaultPath,
    location,
    componentTabs,
    appName,
    roles,
    userInfo,
    userConfig,
    userDropDownItems,
    dropDownMenus,
}: ActionMemoNavProps) => {
    const { instance, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();

    const getLoginElement = () => {
        let username = '';

        if (userInfo) {
            username = userInfo.displayName;
        }

        return isAuthenticated ? (
            <NavDropdown.Item as="button" onClick={() => instance.logoutRedirect()}>
                {`${username} (Logout)`}
            </NavDropdown.Item>
        ) : (
            <NavDropdown.Item
                as="button"
                onClick={() => instance.loginRedirect({ ...GRAPH_API_REQUEST })}
            >
                Login
            </NavDropdown.Item>
        );
    };

    const shouldRender = (componentRoles: Array<string> | null) =>
        !componentRoles || intersection(roles, componentRoles).length > 0;

    let tabs: Array<React.ReactNode> = [];
    if (isAuthenticated && inProgress === InteractionStatus.None) {
        tabs = componentTabs.map((item) => {
            if (
                item.roles === null ||
                item.roles.length === 0 ||
                intersection(roles, item.roles).length > 0
            ) {
                const tabPath = item.components[0].path;
                if (item.components.length === 1) {
                    // If there was only one item, display it as a tab
                    const component = item.components[0];
                    if (shouldRender(component.roles)) {
                        const linkPath = component.entryPath ? component.entryPath : tabPath;
                        return (
                            <Nav.Link
                                as={Link}
                                to={linkPath}
                                key={item.name}
                                active={NavUtils.locationInPath(location.pathname, tabPath)}
                            >
                                {item.item ? item.item : item.name}
                            </Nav.Link>
                        );
                    }
                }

                const dropdownRoutes: Array<string> = [];

                // If there was more than one item, it's a drop down
                const dropdownItems = item.components.map((component) => {
                    if (shouldRender(component.roles)) {
                        if (component.path) {
                            dropdownRoutes.push(component.path);
                            const linkPath = component.entryPath
                                ? component.entryPath
                                : component.path;
                            return (
                                <NavDropdown.Item as={Link} to={linkPath} key={component.name}>
                                    {component.name}
                                </NavDropdown.Item>
                            );
                        }
                        return component.component;
                    }
                    return null;
                });

                // Check to see if a route in the current tab matches the current location
                let active = false;

                dropdownRoutes.forEach((route) => {
                    if (NavUtils.locationInPath(location.pathname, route)) {
                        active = true;
                    }
                });

                return (
                    <NavDropdown
                        id={item.name}
                        title={item.name}
                        active={active}
                        key={item.name}
                        className={item.navCssClass}
                    >
                        {dropdownItems}
                    </NavDropdown>
                );
            }
            return '';
        });
    }

    let environment: string | React.ReactElement | null = getEnvironmentName();
    if (environment) {
        environment = (
            <Badge id="env-badge" variant="warning" className="text-light">
                {environment}
            </Badge>
        );
    }

    const profile = useMemo(() => {
        let userEmail = '';
        if (userInfo) {
            userEmail = userInfo.userPrincipalName;
            return (
                <div className="profile-photo d-inline ml-3">
                    <Portrait username={userEmail} roundedCircle />
                </div>
            );
        }
        return <i className="fas fa-user fa-lg" data-testid="user-dropdown-btn" />;
    }, [userInfo]);

    return (
        <Navbar
            id="lorule-nav"
            data-testid="loruleNav"
            variant="dark"
            className="lorule-nav-bar"
            expand="lg"
        >
            <Container fluid>
                <Navbar.Brand href={defaultPath} data-testid="logo-btn" as={Link} to={defaultPath}>
                    <span>
                        <img
                            className="pr-2 d-none d-sm-inline-block mb-2"
                            height="30"
                            alt="Nintendo"
                            src={require('../../resources/images/nintendo-logo-racetrack.svg')}
                        />
                    </span>
                    <span id="app-name">{appName}</span> {environment}
                </Navbar.Brand>
                <Navbar.Toggle aria-controls="navbar-content" />
                <Navbar.Collapse id="navbar-content">
                    <Nav className="mr-auto">{tabs}</Nav>
                    <Nav>{getAdditionalDropDownMenus(roles, dropDownMenus)}</Nav>
                    <Nav>
                        <NavDropdown
                            title={profile}
                            id="user-settings-dropdown"
                            data-testid="user-dropdown"
                            alignRight
                        >
                            {getAdditionalUserDropDownItems(roles, userDropDownItems)}
                            <NavDropdown.Item as={Link} to={userConfig.path}>
                                {userConfig.name}
                            </NavDropdown.Item>
                            <NavDropdown.Divider />
                            {getLoginElement()}
                        </NavDropdown>
                    </Nav>
                </Navbar.Collapse>
            </Container>
        </Navbar>
    );
};

export default ActionMemoNavHeader;
